import { db } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

/**
 * Track when a user downloads/saves a photo
 * This detects long-press on mobile and right-click on desktop
 */
export const trackPhotoDownload = async (photoUrl, galleryName, user) => {
    try {
        // Get user agent and basic info
        const userAgent = navigator.userAgent;
        const timestamp = new Date();
        
        // Create download event data
        const downloadEvent = {
            userId: user?.uid || 'anonymous',
            userEmail: user?.email || 'unknown',
            photoUrl: photoUrl,
            galleryName: galleryName || 'Unknown Gallery',
            userAgent: userAgent,
            timestamp: serverTimestamp(),
            ipAddress: 'Unknown', // Would need server-side tracking for real IP
            location: 'Unknown', // Would need geolocation API for real location
            downloadMethod: 'context_menu', // or 'long_press'
            deviceType: getDeviceType(userAgent)
        };

        // Log to Firestore
        await addDoc(collection(db, 'photo_downloads'), downloadEvent);
    } catch (error) {
        console.error('Error tracking photo download:', error);
    }
};

/**
 * Detect device type from user agent
 */
const getDeviceType = (userAgent) => {
    if (userAgent.includes('iPhone')) return 'iPhone';
    if (userAgent.includes('iPad')) return 'iPad';
    if (userAgent.includes('Android')) return 'Android';
    if (userAgent.includes('Windows')) return 'Windows';
    if (userAgent.includes('Mac')) return 'Mac';
    return 'Unknown';
};

/**
 * Add download tracking to an image element
 * Call this function on each image in your galleries
 * Only tracks actual download attempts, not zoom/scroll interactions
 */
export const addDownloadTracking = (imgElement, photoUrl, galleryName, user) => {
    if (!imgElement) return;

    // Track right-click (desktop) - most reliable download indicator
    imgElement.addEventListener('contextmenu', (e) => {
        trackPhotoDownload(photoUrl, galleryName, user);
    });

    // Track long-press (mobile) - reliable download indicator
    let touchStartTime = 0;
    let touchEndTime = 0;
    const longPressThreshold = 800; // Increased to 800ms to avoid accidental triggers

    imgElement.addEventListener('touchstart', (e) => {
        touchStartTime = Date.now();
    });

    imgElement.addEventListener('touchend', (e) => {
        touchEndTime = Date.now();
        const pressDuration = touchEndTime - touchStartTime;
        
        if (pressDuration >= longPressThreshold) {
            trackPhotoDownload(photoUrl, galleryName, user);
        }
    });

    // Track when user tries to drag the image (common download method)
    imgElement.addEventListener('dragstart', (e) => {
        trackPhotoDownload(photoUrl, galleryName, user);
    });

    // Removed selectstart and focus events as they fire on zoom/scroll
};

/**
 * Track download attempts via beforeunload (when user tries to save)
 * This is less reliable but can catch some download attempts
 */
export const trackDownloadAttempt = (photoUrl, galleryName, user) => {
    const handleBeforeUnload = () => {
        // Check if user is trying to save the image
        if (document.activeElement?.tagName === 'IMG') {
            trackPhotoDownload(photoUrl, galleryName, user);
        }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    // Clean up listener after 5 seconds
    setTimeout(() => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
    }, 5000);
};

/**
 * Enhanced tracking with visibility API
 * Tracks when user focuses on an image (potential download intent)
 * DISABLED - This was causing too many false positives on zoom/scroll
 */
export const addEnhancedTracking = (imgElement, photoUrl, galleryName, user) => {
    // Disabled to prevent false positives from zoom/scroll interactions
    // The basic tracking (contextmenu, long-press, dragstart) is sufficient
    return;
};
